import {useMergeRefs} from '@chakra-ui/react';
import {ChakraUl} from '@linx-security/ui/utils/chakra-elements';
import type {MenuListProps} from 'chakra-react-select';
import AutoSizer from 'react-virtualized-auto-sizer';
import {VariableSizeList} from 'react-window';
import {useEffect, useRef, useMemo} from 'react';

import {getListHeight, getListWidth} from '../helpers';
import {parts} from '../theme/anatomy';
import {useStyles} from '../theme/context';

import {DEFAULT_HEIGHT, DEFAULT_WIDTH, CustomScrollbars, Row} from './Row';

export const MenuList = (props: MenuListProps<any, any, any>) => {
  const {
    options,
    maxHeight,
    children,
    selectProps: {chakraStyles, inputValue, onMenuScrollToBottom},
  } = props;
  // @ts-expect-error: we are using a custom prop here
  const optionHeight = props.selectProps.optionHeight ?? DEFAULT_HEIGHT;
  const {menuList} = useStyles();
  const listRef = useRef<VariableSizeList | null>(null);
  const rowHeights = useRef<Record<number, number>>({});
  const mergedRef = useMergeRefs(listRef);
  const sx = chakraStyles?.menuList?.({}, props) ?? {};

  useEffect(() => {
    listRef.current?.scrollToItem(0, `start`);
  }, [inputValue]);

  const getRowHeight = (index: number) => {
    return rowHeights.current[index] || optionHeight;
  };

  const setRowHeight = (index: number, size: number) => {
    listRef.current?.resetAfterIndex(0);
    rowHeights.current = {...rowHeights.current, [index]: size};
  };

  const defaultHeight = useMemo(
    () =>
      getListHeight({
        maxHeight,
        totalSize: options.length,
        optionHeight,
      }),
    [maxHeight, options.length, optionHeight],
  );
  const defaultWidth = useMemo(
    () =>
      getListWidth({
        maxWidth: DEFAULT_WIDTH,
        totalSize: options.length,
        optionWidth: DEFAULT_WIDTH,
      }),
    [options.length],
  );

  const visibleOptions = useMemo(() => {
    return options.filter(option => !option.__isNew__);
  }, [options]);

  return (
    <ChakraUl
      {...parts.menuList.attrs}
      __css={{
        ...menuList,
        ...sx,
      }}
    >
      {/* // We render the children as react-select provided us if there are no options (since it contains the No Result & Loading components) */}
      {visibleOptions.length ? (
        <div style={{display: `block`, height: defaultHeight}}>
          <AutoSizer defaultHeight={defaultHeight} defaultWidth={defaultWidth}>
            {({height, width}) => {
              // Ensure width takes full available space
              const fullWidth = Math.max(width, defaultWidth);
              // Ensure height takes full available space
              const fullHeight = Math.max(height, defaultHeight);
              return (
                <VariableSizeList
                  outerElementType={CustomScrollbars}
                  estimatedItemSize={optionHeight}
                  ref={mergedRef}
                  width={fullWidth}
                  height={fullHeight}
                  onItemsRendered={({visibleStopIndex}) => {
                    if (visibleStopIndex >= visibleOptions.length - 5 && onMenuScrollToBottom) {
                      onMenuScrollToBottom(
                        new WheelEvent(`wheel`, {
                          // TODO: pass the event data from the ref
                        }),
                      );
                    }
                  }}
                  itemSize={getRowHeight}
                  itemCount={visibleOptions.length}
                >
                  {({index, style, data}) => {
                    const option = visibleOptions[index];
                    if (!option) return null;
                    return (
                      <Row
                        isFirst={index === 0}
                        isLast={index === visibleOptions.length - 1}
                        data={data}
                        key={index}
                        index={index}
                        style={style}
                        setRowHeight={setRowHeight}
                      >
                        {children?.[index as keyof typeof children] ?? ``}
                      </Row>
                    );
                  }}
                </VariableSizeList>
              );
            }}
          </AutoSizer>
        </div>
      ) : (
        children
      )}
    </ChakraUl>
  );
};
